import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { usePartnerDetails } from '@/hooks/usePartnerDetails';

interface LogoContextType {
  logoUrl?: string;
  companyName: string;
  isWhiteLabel: boolean;
}

const LogoContext = createContext<LogoContextType>({
  logoUrl: undefined,
  companyName: 'Schedule Hero AI',
  isWhiteLabel: false,
});

export function LogoProvider({ children }: { children: React.ReactNode }) {
  const { user, profile } = useAuth();
  const { data: partnerDetails } = usePartnerDetails();
  
  // Stable values - only change when logo actually changes
  const [stableLogoUrl, setStableLogoUrl] = useState<string | undefined>(undefined);
  const [stableCompanyName, setStableCompanyName] = useState('Schedule Hero AI');
  const [stableIsWhiteLabel, setStableIsWhiteLabel] = useState(false);

  useEffect(() => {
    if (!user || !profile) return;

    const isWhiteLabel = partnerDetails?.partner_type === 'white_label';
    
    let logoUrl: string | undefined;
    let companyName = 'Schedule Hero AI';

    if (isWhiteLabel && partnerDetails?.branding_settings) {
      logoUrl = partnerDetails.branding_settings.logoUrl;
      companyName = partnerDetails.branding_settings.companyName || 'Schedule Hero AI';
    }

    // Only update on actual changes
    if (stableLogoUrl !== logoUrl) {
      setStableLogoUrl(logoUrl);
    }
    if (stableCompanyName !== companyName) {
      setStableCompanyName(companyName);
    }
    if (stableIsWhiteLabel !== isWhiteLabel) {
      setStableIsWhiteLabel(isWhiteLabel);
    }
  }, [user, profile, partnerDetails, stableLogoUrl, stableCompanyName, stableIsWhiteLabel]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    logoUrl: stableLogoUrl,
    companyName: stableCompanyName,
    isWhiteLabel: stableIsWhiteLabel,
  }), [stableLogoUrl, stableCompanyName, stableIsWhiteLabel]);

  return (
    <LogoContext.Provider value={contextValue}>
      {children}
    </LogoContext.Provider>
  );
}

export const useLogo = () => {
  const context = useContext(LogoContext);
  if (!context) {
    throw new Error('useLogo must be used within a LogoProvider');
  }
  return context;
};